import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import { useMenu } from "../../hooks/useMenu";
import { formatCents } from "../../utils/money";
import { ONLINE_ORDERING_ENABLED } from "../../utils/constants";

// ── Image placeholder ──────────────────────────────────────────────────────

/** TCSB-branded placeholder for items without an image. */
function ImagePlaceholder({ name }) {
  return (
    <div
      className="w-full h-40 bg-charcoal flex items-center justify-center rounded-t-2xl"
      aria-hidden="true"
    >
      <span className="font-display text-aluminum/40 text-4xl text-center px-2 leading-relaxed">
        {name?.charAt(0) ?? "☕"}
      </span>
    </div>
  );
}

// ── Menu item card ─────────────────────────────────────────────────────────

/** Card linking to the item detail page. */
function MenuItemCard({ item }) {
  return (
    <Link
      to={`/menu/${item.id}`}
      data-testid={`menu-item-card-${item.id}`}
      className="block bg-white border-2 border-charcoal rounded-2xl overflow-hidden shadow-[4px_4px_0_rgba(43,39,36,0.12)] hover:-translate-y-0.5 transition-transform group"
    >
      {item.imageUrl ? (
        <img
          src={item.imageUrl}
          alt={item.name}
          className="w-full h-40 object-cover"
        />
      ) : (
        <ImagePlaceholder name={item.name} />
      )}
      <div className="p-4">
        <h3 className="font-display text-xl text-charcoal uppercase group-hover:text-teal transition-colors line-clamp-1">
          {item.name}
        </h3>
        {item.description && (
          <p className="font-body text-ink/70 text-sm mt-1 line-clamp-2 leading-snug">
            {item.description}
          </p>
        )}
        <p
          className="font-display text-teal text-xl mt-2"
          data-testid={`item-price-${item.id}`}
        >
          {formatCents(item.priceCents)}
        </p>
      </div>
    </Link>
  );
}

// ── Skeleton grid ──────────────────────────────────────────────────────────

function MenuSkeleton() {
  return (
    <div className="min-h-screen bg-cream" data-testid="menu-skeleton">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <Skeleton height={40} width={200} className="mb-6" />
        {[1, 2].map((s) => (
          <div key={s} className="mb-12">
            <Skeleton height={28} width={150} className="mb-4" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="rounded-2xl overflow-hidden border-2 border-charcoal/20">
                  <Skeleton height={160} />
                  <div className="p-4">
                    <Skeleton height={20} width="60%" />
                    <Skeleton height={14} count={2} className="mt-1" />
                    <Skeleton height={18} width="30%" className="mt-2" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Category tab bar ───────────────────────────────────────────────────────

/** Horizontally scrollable tab bar, one tab per category. */
function CategoryTabs({ categories, activeId, onSelect }) {
  return (
    <nav
      className="sticky top-16 z-30 bg-cream border-b-2 border-charcoal overflow-x-auto no-scrollbar"
      aria-label="Menu categories"
    >
      <div className="flex gap-2 max-w-6xl mx-auto px-4 py-3">
        {categories.map((cat) => (
          <button
            key={cat.id}
            data-testid={`category-tab-${cat.id}`}
            onClick={() => onSelect(cat.id)}
            className={[
              "flex-shrink-0 px-5 py-2 font-heading tracking-widest text-sm rounded-full border-2 border-charcoal transition-colors",
              activeId === cat.id
                ? "bg-rust text-white"
                : "bg-white text-charcoal hover:bg-cream-deep",
            ].join(" ")}
          >
            {cat.name.toUpperCase()}
          </button>
        ))}
      </div>
    </nav>
  );
}

// ── Category section ──────────────────────────────────────────────────────

/** Section anchor + heading + item grid for one category. */
function CategorySection({ category }) {
  if (!category.items?.length) return null;

  return (
    <section
      id={`category-${category.id}`}
      aria-labelledby={`cat-heading-${category.id}`}
      className="mb-14"
      data-testid={`category-section-${category.id}`}
    >
      <h2
        id={`cat-heading-${category.id}`}
        className="font-display text-3xl text-charcoal uppercase mb-5"
      >
        {category.name}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {category.items.map((item) => (
          <MenuItemCard key={item.id} item={item} />
        ))}
      </div>
    </section>
  );
}

// ── Error state ───────────────────────────────────────────────────────────

function MenuError({ onRetry }) {
  return (
    <main data-testid="menu-error" className="min-h-screen bg-cream flex items-center justify-center">
      <div className="text-center px-4">
        <p className="font-body text-ink/70 text-lg mb-4">
          Unable to load the menu right now.
        </p>
        <button
          onClick={onRetry}
          className="font-heading tracking-widest bg-rust text-white px-7 py-3 rounded-xl shadow-[0_4px_0_#7f371e] hover:translate-y-0.5 hover:shadow-[0_2px_0_#7f371e] transition-all"
        >
          TRY AGAIN
        </button>
      </div>
    </main>
  );
}

// ── Menu page ─────────────────────────────────────────────────────────────

/**
 * Menu — Public menu browsing page.
 * Displays categories as a sticky tab bar; clicking a tab scrolls to that section.
 * Items are displayed in a responsive card grid with price formatted from cents.
 */
export default function Menu() {
  const { categories, error, isLoading, mutate } = useMenu();
  const [activeTab, setActiveTab] = useState(null);
  const sectionRefs = useRef({});

  const handleTabClick = (catId) => {
    setActiveTab(catId);
    const el = document.getElementById(`category-${catId}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (isLoading) return <MenuSkeleton />;
  if (error) return <MenuError onRetry={mutate} />;
  if (!categories.length) {
    return (
      <main data-testid="menu-page" className="min-h-screen bg-cream flex items-center justify-center">
        <p className="font-body text-ink/60 text-lg">No items available right now.</p>
      </main>
    );
  }

  const active = activeTab ?? categories[0]?.id;

  return (
    <main data-testid="menu-page" className="min-h-screen bg-cream">
      <CategoryTabs
        categories={categories}
        activeId={active}
        onSelect={handleTabClick}
      />

      <div className="max-w-6xl mx-auto px-4 py-8">
        <p className="font-heading tracking-[0.3em] text-teal-deep text-sm">FROM THE BULLET</p>
        <h1
          className={`font-display text-5xl text-charcoal uppercase ${
            ONLINE_ORDERING_ENABLED ? "mb-8" : "mb-2"
          }`}
        >
          Our Menu
        </h1>

        {/* SPR-014 T-007: temporary, flag-gated launch notice. Online ordering
            is disabled at launch — flip ONLINE_ORDERING_ENABLED to hide. */}
        {!ONLINE_ORDERING_ENABLED && (
          <p
            className="font-body text-ink/60 text-base mb-8"
            data-testid="menu-coming-soon-banner"
          >
            Browse our menu — online ordering coming soon.
          </p>
        )}

        {categories.map((cat) => (
          <div key={cat.id} ref={(el) => { sectionRefs.current[cat.id] = el; }}>
            <CategorySection category={cat} />
          </div>
        ))}
      </div>
    </main>
  );
}
